import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import SendWhatsAppModal from "./SendWhatsAppModal";
import {
  MessageSquare, Send, Clock, ChevronDown, ChevronUp, Loader2,
  RefreshCcw, AlertTriangle, CheckCircle2, User,
} from "lucide-react";

/**
 * Card de "Comunicação" na ficha do cliente.
 *
 * Props:
 *  - client: cliente atual (id, full_name, whatsapp)
 *  - contract: contrato opcional para anexar ao envio (id, contract_type, ...)
 *
 * Exibe:
 *  - Última mensagem enviada (conteúdo curto, data, status)
 *  - Botão "Enviar nova mensagem"
 *  - Toggle "Ver histórico completo" — thread cronológica
 */
export default function ClientCommunicationCard({ client, contract = null }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [openSend, setOpenSend] = useState(false);

  const load = async () => {
    if (!client?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/history/client/${client.id}`, { params: { limit: 200 } });
      setItems(data.items || []);
    } catch (err) {
      // 403 (sócio sem acesso) ou 404 — silencioso, exibe estado vazio
      setItems([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client?.id]);

  const last = items.length ? items[items.length - 1] : null;

  const statusPill = (s) => {
    const map = {
      read:                  { l: "Lido",         c: "text-emerald-300 border-emerald-500/40 bg-emerald-500/5" },
      delivered:             { l: "Entregue",     c: "text-blue-300 border-blue-500/40 bg-blue-500/5" },
      sent:                  { l: "Enviado",      c: "text-cyan-300 border-cyan-500/40 bg-cyan-500/5" },
      pending:               { l: "Pendente",     c: "text-[#A3A3A3] border-[#3a3a3a] bg-[#0a0a0a]" },
      awaiting_confirmation: { l: "Aguarda conf.",c: "text-amber-300 border-amber-500/40 bg-amber-500/5" },
      error:                 { l: "Erro",         c: "text-red-300 border-red-500/40 bg-red-500/5" },
    };
    const m = map[s] || map.pending;
    return <span className={`text-[10px] px-1.5 py-0.5 rounded border ${m.c}`}>{m.l}</span>;
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    try {
      const dt = new Date(iso);
      const diff = (Date.now() - dt.getTime()) / 1000;
      if (diff < 60) return "agora";
      if (diff < 3600) return `${Math.floor(diff / 60)} min atrás`;
      if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
      if (diff < 604800) return `${Math.floor(diff / 86400)}d atrás`;
      return dt.toLocaleDateString("pt-BR");
    } catch { return ""; }
  };
  const fmt = (iso) => (iso || "").replace("T", " ").slice(0, 16);

  return (
    <>
      <section className="fc-card p-5 sm:p-6" data-testid="client-communication-card">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-[15px] font-semibold tracking-tight flex items-center gap-2">
            <MessageSquare size={15} className="text-emerald-400"/> Comunicação
          </h2>
          <button onClick={load} className="text-[#737373] hover:text-emerald-300" title="Recarregar"><RefreshCcw size={12}/></button>
        </div>

        {loading ? (
          <div className="py-6 text-center"><Loader2 size={16} className="animate-spin mx-auto text-[#737373]"/></div>
        ) : !last ? (
          <div className="text-center py-4">
            <div className="text-[12px] text-[#A3A3A3] mb-3">Nenhuma mensagem enviada ainda.</div>
            <button
              onClick={() => setOpenSend(true)}
              disabled={!client?.whatsapp}
              className="fc-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
              data-testid="client-com-send-first"
            >
              <Send size={12}/> Enviar primeira mensagem
            </button>
            {!client?.whatsapp && <div className="text-[10.5px] text-amber-300 mt-2">Cadastre o WhatsApp do cliente para poder enviar.</div>}
          </div>
        ) : (
          <>
            <div className="rounded-md border border-[#1f1f1f] bg-[#0a0a0a] p-3" data-testid="client-com-last">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10.5px] uppercase tracking-wider text-[#737373] flex items-center gap-1.5">
                  <Clock size={10}/> Última mensagem · {timeAgo(last.created_at)}
                </div>
                {statusPill(last.status)}
              </div>
              <div className="text-[12px] text-[#E5E5E5] whitespace-pre-wrap line-clamp-3 mb-1.5">{last.content}</div>
              <div className="flex items-center justify-between text-[10.5px] text-[#737373]">
                <span>{last.template_name || last.template_code || "livre"}</span>
                <span className="font-mono-tab">{fmt(last.created_at)}</span>
              </div>
            </div>

            <div className="flex items-center justify-between mt-3 gap-2">
              <button
                onClick={() => setShowAll(v => !v)}
                className="text-[11.5px] text-[#A3A3A3] hover:text-emerald-300 flex items-center gap-1"
                data-testid="client-com-toggle-thread"
              >
                {showAll ? <><ChevronUp size={12}/> Ocultar histórico</> : <><ChevronDown size={12}/> Ver conversa completa ({items.length})</>}
              </button>
              <button
                onClick={() => setOpenSend(true)}
                disabled={!client?.whatsapp}
                className="fc-btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-40"
                data-testid="client-com-send-new"
              >
                <Send size={12}/> Nova mensagem
              </button>
            </div>

            {showAll && (
              <div className="mt-3 pt-3 border-t border-[#1a1a1a] space-y-2 max-h-[480px] overflow-y-auto" data-testid="client-com-thread">
                {items.map(m => (
                  <div key={m.id} className="rounded border border-[#1f1f1f] bg-[#080808] p-2.5" data-testid={`client-com-msg-${m.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5 text-[10.5px] text-[#A3A3A3]">
                        <User size={9} className="text-[#737373]"/>
                        <span>{m.sender_name || "Sistema"}</span>
                        <span className="text-[#3a3a3a]">·</span>
                        <span className="font-mono-tab">{fmt(m.created_at)}</span>
                        {m.origin === "automatic" && <span className="text-[9px] uppercase text-purple-300">auto</span>}
                        {m.origin === "bulk" && <span className="text-[9px] uppercase text-blue-300">massa</span>}
                      </div>
                      {statusPill(m.status)}
                    </div>
                    <div className="text-[11.5px] text-[#E5E5E5] whitespace-pre-wrap leading-relaxed">{m.content}</div>
                    {m.template_name && <div className="text-[10px] text-[#737373] mt-1">via <em>{m.template_name}</em></div>}
                    {m.error_message && <div className="text-[10.5px] text-red-300 mt-1 flex items-start gap-1"><AlertTriangle size={10} className="mt-0.5"/> {m.error_message}</div>}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <SendWhatsAppModal
        open={openSend}
        onClose={() => setOpenSend(false)}
        clients={[{ id: client.id, full_name: client.full_name, whatsapp: client.whatsapp }]}
        contract={contract}
        onSent={() => { setOpenSend(false); load(); toast.success("Mensagem enviada. Atualizando conversa..."); }}
      />
    </>
  );
}
